// frontend/packages/business-console/src/components/content/MobileEditorPane.jsx
//
// Simplified editor for mobile devices. Supports:
//   - Text field editing (title, description, headlines)
//   - Image upload/replace via native file picker
//   - Blog post creation (title, body, cover image)
//   - Save Draft and Save & Publish
//
// Does NOT support:
//   - Sections editing (too complex for small screens)
//   - Settings structure changes
//   - Drag/reorder
//
// Shows a message for unsupported features directing users to desktop.

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import FrontmatterField from './FrontmatterField';
import SeoAssistCard from './SeoAssistCard';
import CopilotBar from './CopilotBar';
import SaveStatusIndicator from './SaveStatusIndicator';
import PublishConfirmDialog from './PublishConfirmDialog';
import {
  buildContentPath,
  stripMarkdownExtension,
  humanizeLabel,
  validateRequired,
  setAtPath,
} from '../../utils/contentEditorHelpers';
import MediaPickerModal from '../media/MediaPickerModal';
import InsertModeDialog from '../media/InsertModeDialog';
import { toCldToken } from '../../utils/mediaToken';
import { markRecentlyUsed } from '../../services/mediaService';

const safe = (v) => (v == null ? '' : String(v));

const MOBILE_SAFE_TYPES = new Set([
  'string',
  'number',
  'boolean',
  'date',
  'enum',
]);
const SECTIONS_FIELD = 'sections';

function isMobileSafeField(key, spec) {
  if (key === SECTIONS_FIELD) return false;
  if (MOBILE_SAFE_TYPES.has(spec?.type)) return true;
  if (spec?.type === 'object' && spec?.fields) return true;
  if (spec?.type === 'array' && spec?.items?.type === 'string') return true;
  return false;
}

function insertAtCursor(textarea, insertText) {
  if (!textarea) return { next: insertText, caret: insertText.length };
  const value = textarea.value || '';
  const start =
    typeof textarea.selectionStart === 'number'
      ? textarea.selectionStart
      : value.length;
  const end =
    typeof textarea.selectionEnd === 'number'
      ? textarea.selectionEnd
      : value.length;
  return {
    next: `${value.slice(0, start)}${insertText}${value.slice(end)}`,
    caret: start + insertText.length,
  };
}

function buildInsertSnippet(asset) {
  const publicId = safe(asset?.blobPath);
  const token = toCldToken(publicId);
  if (!token) return '';
  const alt = safe(asset?.altText || '').replace(/\]/g, '\\]');
  return `\n![${alt || 'Image'}](${token})\n`;
}

export default function MobileEditorPane({
  mode,
  selectedFile,
  schemaFields,
  frontmatterObj,
  markdownBody,
  dirty,
  loadingFile,
  saving,
  deleting,
  publishing,
  publishStatus,
  publishSuccess,
  publishConfirmSignal,
  onDismissPublishSuccess,
  error,
  onFrontmatterChange,
  onMarkdownChange,
  onSaveDraft,
  onPublish,
  onDelete,
  createCollection,
}) {
  const [createFilename, setCreateFilename] = useState('');
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState({ kind: 'markdown' });
  const [pendingAsset, setPendingAsset] = useState(null);
  const [insertModeOpen, setInsertModeOpen] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const markdownRef = useRef(null);

  const isBusy = saving || deleting || publishing || loadingFile;
  const isSettingsFile =
    selectedFile?.collection === 'settings' || createCollection === 'settings';
  const expectedDelete =
    selectedFile?.name || selectedFile?.path?.split('/').pop() || '';

  const missingRequired = useMemo(() => {
    if (loadingFile || !schemaFields) return [];
    return validateRequired(frontmatterObj, schemaFields);
  }, [frontmatterObj, loadingFile, schemaFields]);

  const title = useMemo(() => {
    if (mode === 'edit')
      return humanizeLabel(
        stripMarkdownExtension(selectedFile?.name || 'Editing')
      );
    if (mode === 'create')
      return createCollection
        ? `New ${humanizeLabel(createCollection)}`
        : 'New';
    return '';
  }, [createCollection, mode, selectedFile?.name]);

  const canSaveDraft = useMemo(() => {
    if (isBusy || !dirty || missingRequired.length > 0) return false;
    if (mode === 'create') return Boolean(safe(createFilename).trim());
    return mode === 'edit';
  }, [createFilename, dirty, isBusy, missingRequired.length, mode]);

  const hasSections = schemaFields && SECTIONS_FIELD in schemaFields;

  const mobileFields = useMemo(() => {
    if (!schemaFields) return [];
    return Object.entries(schemaFields).filter(([key, spec]) =>
      isMobileSafeField(key, spec)
    );
  }, [schemaFields]);

  // Returns whatever onSaveDraft reports (false when the save failed), so
  // handleSaveAndPublish can stop instead of confirming a publish that cannot
  // succeed.
  const handleSaveDraft = async () => {
    if (mode === 'create') {
      const fn = safe(createFilename).trim();
      return onSaveDraft({
        mode: 'create',
        path: buildContentPath(createCollection, fn),
      });
    }
    return onSaveDraft({ mode: 'edit', path: selectedFile?.path });
  };

  const handleSaveAndPublish = useCallback(async () => {
    // Don't chain a publish onto a save that failed: the save's error is
    // already on screen, and confirming would only fail again.
    const saved = await handleSaveDraft();
    if (saved === false) return;
    setPublishConfirmOpen(true);
  }, [handleSaveDraft]);

  // Same confirmation the button opens, for the Content page's publish
  // keyboard shortcut.
  //
  // Keyed on the signal alone on purpose: it must fire when the shortcut
  // fires, not whenever busy/dirty/publish state shifts underneath it. The
  // other values are read as a guard at that moment, not subscribed to.
  useEffect(() => {
    if (!publishConfirmSignal) return;
    if (isBusy || dirty) return;
    if (Number(publishStatus?.ahead_by ?? 0) <= 0) return;
    setPublishConfirmOpen(true);
  }, [publishConfirmSignal]);

  const handleConfirmPublish = useCallback(() => {
    setPublishConfirmOpen(false);
    onPublish?.();
  }, [onPublish]);

  const openMediaForMarkdown = () => {
    setMediaTarget({ kind: 'markdown' });
    setMediaOpen(true);
  };
  const openMediaForFrontmatter = ({ path }) => {
    setMediaTarget({ kind: 'frontmatter', path });
    setMediaOpen(true);
  };

  const applyFrontmatterToken = useCallback(
    (path, token) => {
      const next = JSON.parse(JSON.stringify(frontmatterObj || {}));
      const segments = String(path).split('.').filter(Boolean);
      let cur = next;
      for (let i = 0; i < segments.length - 1; i++) {
        const k = segments[i];
        if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {};
        cur = cur[k];
      }
      cur[segments[segments.length - 1]] = token;
      onFrontmatterChange(next);
    },
    [frontmatterObj, onFrontmatterChange]
  );

  const handleMediaPicked = useCallback(
    async (asset) => {
      if (!asset) return;
      try {
        const pid = safe(asset?.blobPath);
        if (pid) await markRecentlyUsed(pid);
      } catch (e) {
        /* best-effort */
      }
      if (mediaTarget.kind === 'frontmatter') {
        const token = toCldToken(safe(asset?.blobPath));
        if (token) applyFrontmatterToken(mediaTarget.path, token);
        return;
      }
      setPendingAsset(asset);
      setInsertModeOpen(true);
    },
    [applyFrontmatterToken, mediaTarget.kind, mediaTarget.path]
  );

  const commitMarkdownInsert = useCallback(
    (insertMode) => {
      const asset = pendingAsset;
      setPendingAsset(null);
      setInsertModeOpen(false);
      if (!asset) return;
      const snippet = buildInsertSnippet(asset);
      if (!snippet) return;
      const textarea = markdownRef.current;
      const { next, caret } = insertAtCursor(textarea, snippet);
      onMarkdownChange(next);
      setTimeout(() => {
        try {
          if (textarea) {
            textarea.focus();
            textarea.selectionStart = caret;
            textarea.selectionEnd = caret;
          }
        } catch (e) {
          /* ignore */
        }
      }, 0);
    },
    [onMarkdownChange, pendingAsset]
  );

  if (mode === 'none') {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ opacity: 0.6, mt: 4 }}>
          Select a file from the menu to start editing.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Typography
        variant="caption"
        sx={{ opacity: 0.5, fontWeight: 600, textTransform: 'uppercase' }}
      >
        {selectedFile?.collection ? humanizeLabel(selectedFile.collection) : ''}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
        {title}
      </Typography>

      <SaveStatusIndicator
        saving={saving}
        dirty={dirty}
        publishing={publishing}
        deleting={deleting}
      />

      {/* Action buttons — stacked for mobile, bottom-anchored feel */}
      <Stack spacing={1} sx={{ mt: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="success"
          fullWidth
          startIcon={<PublishIcon />}
          disabled={!canSaveDraft}
          onClick={handleSaveAndPublish}
          sx={{ fontWeight: 900, py: 1.5 }}
        >
          {saving
            ? 'Saving...'
            : publishing
              ? 'Publishing...'
              : 'Save & Publish'}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<SaveIcon />}
            disabled={!canSaveDraft}
            onClick={handleSaveDraft}
            sx={{ fontWeight: 900 }}
          >
            Save Draft
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PhotoLibraryOutlinedIcon />}
            disabled={isBusy}
            onClick={openMediaForMarkdown}
            sx={{ fontWeight: 900 }}
          >
            Add Image
          </Button>
        </Stack>
      </Stack>

      {/* Status messages */}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {publishSuccess ? (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={onDismissPublishSuccess}
        >
          Published! Changes will appear on your site in 1-2 minutes.
        </Alert>
      ) : null}
      {missingRequired.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please fill in: {missingRequired.join(', ')}
        </Alert>
      ) : null}

      {/* Loading */}
      {isBusy && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {saving ? 'Saving...' : publishing ? 'Publishing...' : 'Loading...'}
          </Typography>
        </Box>
      )}

      {/* Create mode: filename input */}
      {mode === 'create' ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <TextField
            label="Name"
            value={createFilename}
            onChange={(e) => setCreateFilename(e.target.value)}
            fullWidth
            size="small"
            helperText="Example: my-new-post"
          />
        </Paper>
      ) : null}

      {/* Lead with AI on mobile too: copilot first, manual fields below. */}
      {schemaFields ? (
        <Box sx={{ mb: 2 }}>
          <CopilotBar
            frontmatterObj={frontmatterObj}
            markdownBody={markdownBody}
            onApplyField={(field, value) =>
              onFrontmatterChange(setAtPath(frontmatterObj, field, value))
            }
            onApplyBody={(value) => onMarkdownChange(value)}
          />
        </Box>
      ) : null}

      {/* Mobile-safe fields only */}
      <Stack spacing={1.5}>
        {mobileFields.map(([key, spec]) => (
          <FrontmatterField
            key={key}
            path={key}
            spec={spec}
            frontmatterObj={frontmatterObj}
            showAllOptional={false}
            onChange={onFrontmatterChange}
            onPickMedia={openMediaForFrontmatter}
          />
        ))}
      </Stack>

      {/* Sections warning */}
      {hasSections ? (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          This page has layout sections that can only be edited on a desktop or
          laptop computer.
        </Alert>
      ) : null}

      {/* Markdown body */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
          Page Content
        </Typography>
        <TextField
          value={markdownBody}
          onChange={(e) => onMarkdownChange(e.target.value)}
          multiline
          minRows={8}
          fullWidth
          placeholder="Start writing..."
          inputRef={markdownRef}
          inputProps={{ style: { fontSize: 14, lineHeight: 1.6 } }}
        />
      </Paper>

      {/* SEO assist (renders only when the schema has title/description) */}
      {schemaFields ? (
        <Box sx={{ mt: 2 }}>
          <SeoAssistCard
            schemaFields={schemaFields}
            frontmatterObj={frontmatterObj}
            markdownBody={markdownBody}
            onApply={(field, value) =>
              onFrontmatterChange(setAtPath(frontmatterObj, field, value))
            }
          />
        </Box>
      ) : null}

      {/* Delete this page */}
      {mode === 'edit' && !isSettingsFile ? (
        <Button
          variant="outlined"
          color="error"
          fullWidth
          startIcon={<DeleteOutlineIcon />}
          disabled={isBusy}
          onClick={() => {
            setDeleteConfirm('');
            setDeleteOpen(true);
          }}
          sx={{ fontWeight: 900, mt: 3 }}
        >
          Delete this page
        </Button>
      ) : null}

      {/* Dialogs */}
      <PublishConfirmDialog
        open={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        onConfirm={handleConfirmPublish}
        publishing={publishing}
      />

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete this page?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            This will permanently remove this file from your website. This
            cannot be undone.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Type <strong>{expectedDelete}</strong> to confirm.
          </Typography>
          <TextField
            sx={{ mt: 2 }}
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            fullWidth
            size="small"
            placeholder="Type filename to confirm"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting || deleteConfirm.trim() !== expectedDelete}
            onClick={async () => {
              await onDelete?.();
              setDeleteOpen(false);
            }}
            sx={{ fontWeight: 900 }}
          >
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
      <MediaPickerModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onPick={async (asset) => {
          await handleMediaPicked(asset);
          setMediaOpen(false);
        }}
      />
      <InsertModeDialog
        open={insertModeOpen}
        asset={pendingAsset}
        onClose={() => {
          setInsertModeOpen(false);
          setPendingAsset(null);
        }}
        onChoose={(m) => commitMarkdownInsert(m)}
      />
    </Box>
  );
}
